import {ANDROID_TABS, PAGE_URLS} from "constants/AppConstants";
import {registerPlugin} from "@capacitor/core";

interface TabBarPluginInterface {
  setBottomBarVisibility: (options: {show: boolean}) => Promise<void>;
  changeTab: (options: {tab: string}) => Promise<void>;
}

const TabBarPlugin = registerPlugin<TabBarPluginInterface>("TabBarPlugin");

function getPathname(): string {
  try {
    return window.location.pathname || "";
  } catch (_) {
    return "";
  }
}

/**
 * Dispatches a `ws-android-back` event so page-level handlers (e.g.
 * ChallengesPage) can dismiss overlays before we fall back to router history.
 *
 * @return {boolean} whether a page handler consumed the back press
 */
export function dispatchAndroidBackEvent(): boolean {
  const detail = {consumed: false};
  try {
    window.dispatchEvent(new CustomEvent("ws-android-back", {detail}));
  } catch (_) {
    // CustomEvent unsupported — fall through
  }
  return detail.consumed;
}

export function dispatchCommunityBackEvent(): boolean {
  return dispatchAndroidBackEvent();
}

function isAccountTabRoute(pathname: string): boolean {
  return (
    pathname === PAGE_URLS.androidSettingsPageUrl ||
    pathname.startsWith(`${PAGE_URLS.androidSettingsPageUrl}/`) ||
    pathname === PAGE_URLS.redeemCode ||
    pathname.startsWith(`${PAGE_URLS.redeemCode}/`) ||
    pathname === PAGE_URLS.favoritesPageUrl ||
    pathname.startsWith(`${PAGE_URLS.favoritesPageUrl}/`) ||
    pathname === PAGE_URLS.myAccountPageUrl ||
    pathname.startsWith(`${PAGE_URLS.myAccountPageUrl}/`)
  );
}

/**
 * Handles the Android hardware back button for the Community tab.
 *
 * Priority:
 *  1. Activity sub-routes opened from the feed/leaderboard -> router goBack
 *  2. Challenges page overlays (leaderboard, modals, etc.) -> ws-android-back
 *  3. Remaining router history within the tab -> goBack
 *
 * @param {object} history react-router history instance
 * @return {boolean} whether the back press was handled
 */
export function handleAndroidBackPress(history: any): boolean {
  const pathname = getPathname();
  const locationState = history.location?.state || {};

  const getCommunityReturnState = () => {
    const originTab = locationState.communityOriginTab || "feed";
    const state: {communityTab: any; challenge?: any} = {communityTab: originTab};
    if (originTab === "challenges" && locationState.communityChallengeId) {
      state.challenge = locationState.communityChallengeId;
    }
    return state;
  };

  if (pathname.startsWith(`${PAGE_URLS.activityPageUrl}/`)) {
    if (locationState.mapFromChallenge) {
      history.replace({
        pathname: PAGE_URLS.challengesPageUrl,
        state: getCommunityReturnState()
      });
      return true;
    }

    // Reached from native Explore — same case safeNavigateBack handles for the
    // on-screen back arrow, needed here too since the hardware/gesture back
    // button goes through this function instead. history.goBack() has no
    // reliable "previous route" to land on here, so hand control back to
    // native and restore the bottom bar the activity page hid on mount.
    if (locationState.mapFromExplore) {
      TabBarPlugin.setBottomBarVisibility({show: true}).catch(() => {});
      TabBarPlugin.changeTab({tab: ANDROID_TABS.EXPLORE}).catch(() => {});
      return true;
    }

    try {
      if (window.history.length > 1) {
        history.goBack();
        return true;
      }
    } catch (_) {
      // fall through to replace below
    }

    if (locationState.mapFromChallenge) {
      history.replace({
        pathname: PAGE_URLS.challengesPageUrl,
        state: getCommunityReturnState()
      });
      return true;
    }
    return false;
  }

  if (pathname === PAGE_URLS.challengesPageUrl || pathname.startsWith(`${PAGE_URLS.challengesPageUrl}/`)) {
    if (dispatchAndroidBackEvent()) {
      return true;
    }

    try {
      if (window.history.length > 1) {
        history.goBack();
        return true;
      }
    } catch (_) {
      // no-op
    }
  }

  if (isAccountTabRoute(pathname)) {
    if (dispatchAndroidBackEvent()) {
      return true;
    }

    if (pathname !== PAGE_URLS.androidSettingsPageUrl) {
      history.replace(PAGE_URLS.androidSettingsPageUrl);
      return true;
    }
  }

  return false;
}
